// What the figures on a cost screen ARE, said above them.
//
// There are two answers and they mean different things. An OPEN month is priced
// from the rollup as it stands, so it says when that was and offers a refresh. A
// CLOSED month has been billed: the report reads it from the amounts that went
// out and never re-evaluates it, so it names the run that closed it and has no
// refresh to offer — there is nothing a refresh could change, and offering one
// would suggest there was.

import { type ReactNode } from "react";
import type { UsageCostResponse, UsageJob } from "@crm/shared";
import styles from "./UsagePage.module.css";
import { formatClock, shortRunId } from "./usageFormat.js";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { useIdempotencyKey } from "../../api/idempotency.js";
import { useStartUsageJobMutation } from "../../api/mutations.js";
import { useHasRole } from "../../auth/useAuth.js";
import { formatCount } from "../../format/money.js";

export interface UsageStatusLineProps {
  readonly report: UsageCostResponse | null;
  readonly period: string;
  readonly deployments: readonly string[];
  readonly tenants: readonly string[];
  readonly onRefreshed: () => void;
}

export function UsageStatusLine({
  report,
  period,
  deployments,
  tenants,
  onRefreshed,
}: UsageStatusLineProps): ReactNode {
  // A refresh spends database time, so it takes the editor role. Hidden rather
  // than disabled for a reader: a disabled button says "you could do this if
  // something changed", which is not what a missing role means.
  const canRefresh = useHasRole("crm_editor");
  const start = useStartUsageJobMutation();
  const { keyFor } = useIdempotencyKey();

  const closed = report !== null && report.provenance.source === "closed";
  const runId = report?.provenance.closed_run_id ?? null;
  const computedAt = report?.provenance.computed_at ?? null;

  const refresh = (): void => {
    const body = {
      kind: "rollup_refresh" as const,
      period,
      deployment_slug: [...deployments],
      tenant_slug: [...tenants],
    };
    start.mutate({ body, idempotencyKey: keyFor(body) }, { onSuccess: () => onRefreshed() });
  };

  return (
    <div className={styles.status}>
      {closed ? (
        <>
          <Badge tone="slate" shape="pill">
            closed, as billed
          </Badge>
          {runId !== null && <span className={styles.runId}>{`run ${shortRunId(runId)}`}</span>}
          <span>
            These are the amounts that went onto a bill. A rate corrected since cannot change them.
          </span>
        </>
      ) : (
        <>
          <Badge tone="info" shape="pill">
            open
          </Badge>
          <span>
            {computedAt === null ? "nothing priced yet" : `priced ${formatClock(computedAt)}`}
          </span>
          <span className={styles.statusSpacer} />
          {canRefresh && (
            <Button variant="secondary" small onClick={refresh} disabled={start.isPending}>
              {start.isPending ? "Queueing…" : "Refresh"}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export interface UsageJobProgressProps {
  readonly job: UsageJob | null;
}

/**
 * A refresh in flight.
 *
 * A progress bar and never a spinner: the figures under it are the previous
 * answer, and a person who can already see roughly the right number can keep
 * working while the rest is recomputed. A failed job says what failed and stays
 * on screen, because a refresh that quietly gave up is a screen that quietly
 * stops being current.
 */
export function UsageJobProgress({ job }: UsageJobProgressProps): ReactNode {
  if (job === null) {
    return (
      <div className={styles.recalculating} role="status">
        <span>Recalculating…</span>
      </div>
    );
  }
  if (job.status === "failed") {
    return (
      <div className={styles.recalculating} role="status">
        <Badge tone="bad" shape="pill">
          refresh failed
        </Badge>
        <span>{job.error ?? "The refresh stopped without saying why."}</span>
      </div>
    );
  }
  const total = job.days_total;
  const share = total === null || total === 0 ? 0 : Math.min(job.days_done / total, 1);
  return (
    <div className={styles.recalculating} role="status">
      <span>Recalculating</span>
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${share * 100}%` }} />
      </div>
      <span>
        {total === null
          ? `${formatCount(job.days_done)} days done`
          : `${formatCount(job.days_done)} of ${formatCount(total)} days`}
      </span>
    </div>
  );
}
